import { Component } from 'react';

import { reportClientError } from '../app/errors/report-client-error.js';
import { ErrorState } from './ErrorState.jsx';

/**
 * Contains a render-time exception to one part of the tree.
 *
 * Used at the root and again around each feature section, so a single bad render
 * degrades one panel instead of unmounting the whole application to a blank white
 * page. The old app had no boundary anywhere: one thrown `toLocaleString` on an
 * undefined value took the entire health record offline with no message and no
 * recovery path.
 *
 * A class component is required here. React exposes error capture only through
 * `getDerivedStateFromError` and `componentDidCatch`, so this is the one place in
 * the codebase that is not a function component.
 *
 * Audit references prevented here: main.jsx #1, food.jsx #13.
 */
export class ErrorBoundary extends Component {
  /**
   * @param {{label: string, children: import('react').ReactNode}} props
   *   `label` names the boundary in the report and in the fallback copy.
   */
  constructor(props) {
    super(props);
    this.state = { error: null, correlationId: null };
    this.handleReset = this.handleReset.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    const correlationId = reportClientError(error, {
      scope: 'react.errorBoundary',
      boundary: this.props.label,
      componentStack: info?.componentStack ?? null,
    });
    this.setState({ correlationId });
  }

  handleReset() {
    this.setState({ error: null, correlationId: null });
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <ErrorState
        title={`${this.props.label} could not be displayed`}
        description="Nothing was lost. Reloading this part of the screen is safe."
        correlationId={this.state.correlationId}
        onRetry={this.handleReset}
        retryLabel="Reload this section"
      />
    );
  }
}
